// 騎士と戴冠獣で、絵の切り替えの瞬間に足・腰・頭が跳ぶ所を、合成の中割りで寄せる比較版 (DEV ?tweenFix=1・2026-10-25)。
//   記録 = docs/research/2026-10-25-knight-hound-tween.md / 計画 = docs/research/2026-10-19-boss-polish-vs-popular-games.md §6 段5。
// やり方: 切り替え直後の数コマだけ、次の絵 B を網目 (三角形) で曲げて描く。部位の点 (兜/頭・腰・前後の足) ごとに、
//   最初のコマは前の絵 A のその部位の画面の位置に置き、決まった時間で B 本来の位置へ寄せる。網目の曲げ方は部位の点からの距離の逆数の重み (IDW)。
//   ・騎士 = 薙ぎの構え直し (lanceSweep_rearm) → 足を寄せる絵 (rest_from_rearm・120ms) → 待機。rest_from_rearm の 120ms を、頭は構え直しに合わせ、
//     終わりは待機の部位の位置に合わせる = 両方の切り替えでずれ 0。
//   ・戴冠獣 = 技の終わりの絵 (跳び込みの着地・払いの当たり・噛みの残り・突進の残り) → 待機。待機の頭の 160ms だけ曲げる。
//   絵の枚数・技の長さ・弾ける時間・当たり (当たりは絵から作っていない) は変えない。描くだけ。指定なし (配布版) は何もしない。
//   「下半身を画素ごと同じに合成する」やり方 (騎士の構え直し 10-08) は、動かない半身にしか効かない。今回の跳びは足も腰も頭も動くので曲げ方にした。
import { KNIGHT_REST_MID_MS, KNIGHT_SWEEP_IDLE_AT_MS } from './knight_rest'

let on = false
/** DEV の URL の値を受け取る (game.ts の DEV ブロックが ?tweenFix=1 で呼ぶ)。 */
export function setTweenFix(v: boolean): void { on = v; tracks.clear() }
export function tweenFixOn(): boolean { return on }

type Pt = readonly [number, number]
type Marks = Readonly<Record<string, Pt>>

// 絵ごとの部位の点 (絵の画素)。scripts/tween_landmarks_1025.json と同じ値 (測る python もこちらを読む)。
//   騎士の構え直しは色を整えた graded/ の絵で読んだ (元の絵と大きさ・形は同じ)。
export const TWEEN_MARKS: Readonly<Record<string, Readonly<Record<string, Marks>>>> = {
  'vigil-knight': {
    lanceSweep_rearm: { helm: [606, 474], hip: [650, 750], heelF: [419, 1273], heelR: [1107, 1272] },
    lanceSweep_rearmMid: { helm: [606, 474], hip: [650, 750], heelF: [419, 1273], heelR: [1107, 1272] },
    rest_from_rearm: { helm: [820, 850], hip: [874, 1093], heelF: [868, 1588], heelR: [1112, 1590] },
    rest_from_rearm_a: { helm: [805, 865], hip: [855, 1180], heelF: [720, 1572], heelR: [1146, 1597] },
    rest_from_rearm_b: { helm: [815, 748], hip: [829, 1105], heelF: [837, 1586], heelR: [1025, 1595] },
    idle: { helm: [196, 340], hip: [200, 575], heelF: [222, 1148], heelR: [275, 1175] },
  },
  'dream-hound': {
    idle: { head: [190, 300], hip: [1130, 330], pawR: [1235, 885], pawF: [235, 860] },
    houndBite_follow: { head: [330, 400], hip: [1060, 330], pawR: [1415, 770], pawF: [60, 790] },
    houndPounce_land: { head: [220, 560], hip: [1100, 250], pawR: [1525, 770], pawF: [40, 800] },
    houndSwipe_contact: { head: [220, 240], hip: [1100, 180], pawR: [1430, 565], pawF: [40, 500] },
    houndLunge_follow: { head: [300, 600], hip: [1130, 200], pawR: [1340, 735], pawF: [60, 745] },
  },
}

type Segment = { key: string; from: readonly string[]; to: string | null; durMs: number; knightRest: boolean }
// 120ms の最後のコマ (60fps で 103〜119ms) で終わりに着くよう、1コマ (17ms) 手前を終わりにする。
export const KNIGHT_REST_TWEEN_MS = KNIGHT_REST_MID_MS - 17
export const HOUND_IDLE_TWEEN_MS = 160
const SEGMENTS: Readonly<Record<string, readonly Segment[]>> = {
  'vigil-knight': [{ key: 'rest_from_rearm', from: ['lanceSweep_rearm', 'lanceSweep_rearmMid'], to: 'idle', durMs: KNIGHT_REST_TWEEN_MS, knightRest: true }],
  'dream-hound': [{
    key: 'idle', from: ['houndPounce_land', 'houndSwipe_contact', 'houndBite_follow', 'houndLunge_follow'], to: null,
    durMs: HOUND_IDLE_TWEEN_MS, knightRest: false,
  }],
}

/** 描画の置き場所 (sprites.ts BossSpritePlacement の要る所だけ)。 */
export type TweenPlacement = {
  imageRect: { x: number; y: number; width: number; height: number }
  pivot: { x: number; y: number }
  facingMul: 1 | -1
  rotationRad: number
}
export type TweenArt = { width: number; height: number; placement: TweenPlacement }

/** 絵の画素 → 画面 (sprites.ts の描き方と同じ順: 足元の軸で左右反転 → 回転)。 */
export function artToScreen(a: TweenArt, p: Pt): [number, number] {
  const { imageRect: r, pivot, facingMul, rotationRad } = a.placement
  const x = r.x + p[0] * r.width / a.width - pivot.x
  const y = r.y + p[1] * r.height / a.height - pivot.y
  const c = Math.cos(rotationRad), s = Math.sin(rotationRad)
  return [pivot.x + facingMul * (c * x - s * y), pivot.y + s * x + c * y]
}
/** 画面 → 絵の画素 (artToScreen の逆)。 */
export function screenToArt(a: TweenArt, q: Pt): [number, number] {
  const { imageRect: r, pivot, facingMul, rotationRad } = a.placement
  const fx = facingMul * (q[0] - pivot.x), fy = q[1] - pivot.y
  const c = Math.cos(rotationRad), s = Math.sin(rotationRad)
  const x = c * fx + s * fy + pivot.x, y = -s * fx + c * fy + pivot.y
  return [(x - r.x) * a.width / r.width, (y - r.y) * a.height / r.height]
}

const smooth = (t: number): number => { const u = Math.max(0, Math.min(1, t)); return u * u * (3 - 2 * u) }

type Track = { lastKey: string | null; lastSec: number; segKey: string | null; fromKey: string | null; startSec: number; startMs: number }
const tracks = new Map<string, Track>()

export type TweenPlan = {
  from: string
  e: number // 0 = 前の絵に合わせた所・1 = 寄せ終わり
  names: string[]
  points: Pt[] // B の絵の部位の点 (絵の画素)
  offsets: [number, number][] // 部位の点を動かす量 (絵の画素)
}

/**
 * 毎コマ、描く絵のキーを渡す。切り替え直後の寄せる区間なら、部位の点と動かす量を返す (それ以外は null)。
 * artOf(key) = そのキーの絵を今のコマで描いた時の置き場所 (前の絵・次の絵の画面の位置を出すため)。
 */
export function tweenFixPlan(
  bossId: string, drawKey: string, timeSec: number,
  restElapsedMs: number | null,
  artOf: (key: string) => TweenArt | null,
): TweenPlan | null {
  if (!on) return null
  const segs = SEGMENTS[bossId]
  if (!segs) return null
  let t = tracks.get(bossId)
  if (!t) { t = { lastKey: null, lastSec: -1, segKey: null, fromKey: null, startSec: 0, startMs: 0 }; tracks.set(bossId, t) }
  // 0.1 秒より空いた (戦いのやり直し・止めていた) 時は、前の絵を引き継がない。
  const fresh = t.lastSec >= 0 && timeSec >= t.lastSec && timeSec - t.lastSec <= 0.1
  const segOf = segs.find(s => s.key === drawKey)
  // 騎士の足を寄せる絵は、ゲームの時計 (出てからの ms・knightRestElapsedMs) で 120ms 出る。戴冠獣は描いた時刻で測る。
  const nowMs = segOf?.knightRest && restElapsedMs != null ? restElapsedMs : timeSec * 1000
  if (drawKey !== t.lastKey) {
    t.segKey = drawKey
    t.fromKey = fresh ? t.lastKey : null
    t.startSec = timeSec
    t.startMs = nowMs
  }
  t.lastKey = drawKey
  t.lastSec = timeSec
  const seg = segOf
  if (!seg || !t.fromKey || !seg.from.includes(t.fromKey)) return null
  // 最初に描いたコマを 0 にする (騎士はゲームの時計で 0〜16ms 遅れて出ることがあるので、その分だけ寄せる時間を縮めて同じ時刻に着かせる)。
  const segMs = nowMs - t.startMs
  const durMs = seg.knightRest && restElapsedMs != null ? Math.max(17, seg.durMs - t.startMs) : seg.durMs
  if (segMs >= durMs && !seg.to) return null
  const marks = TWEEN_MARKS[bossId]
  const mB = marks?.[drawKey], mA = marks?.[t.fromKey]
  const artB = artOf(drawKey), artA = artOf(t.fromKey)
  if (!mB || !mA || !artA || !artB) return null
  const mC = seg.to ? marks[seg.to] : undefined
  const artC = seg.to ? artOf(seg.to) : null
  const e = smooth(segMs / durMs)
  const names = Object.keys(mB).filter(n => mA[n] && (!seg.to || (mC && mC[n])))
  const points = names.map(n => mB[n])
  const offsets = names.map((n): [number, number] => {
    const p = mB[n]
    const a = screenToArt(artB, artToScreen(artA, mA[n]))
    let ex = 0, ey = 0
    if (seg.to && mC && artC) {
      const c = screenToArt(artB, artToScreen(artC, mC[n]))
      ex = c[0] - p[0]; ey = c[1] - p[1]
    }
    return [(1 - e) * (a[0] - p[0]) + e * ex, (1 - e) * (a[1] - p[1]) + e * ey]
  })
  return { from: t.fromKey, e, names, points, offsets }
}

/**
 * 騎士の足を寄せる絵 (rest_from_rearm) が出てからのゲームの時計 (ms)。出る道は3つ (knight_rest.ts):
 *   薙ぎの recovery の 640ms から (knightRestMidRearmKey) / recovery → idle の頭 (restMidRemainingMs) / recovery → telegraph の頭 (restMidTeleRemainingMs)。
 */
export function knightRestElapsedMs(
  boss: { id: string; state: string; stateElapsed: number; currentAttack?: { id: string } | null },
  ss: { restMidKey?: string; restMidRemainingMs?: number; restMidTeleKey?: string; restMidTeleRemainingMs?: number },
): number | null {
  if (boss.id !== 'vigil-knight') return null
  if (boss.state === 'recovery' && boss.currentAttack?.id === 'lanceSweep') return boss.stateElapsed * 1000 - KNIGHT_SWEEP_IDLE_AT_MS
  if (boss.state === 'idle' && ss.restMidKey === 'rest_from_rearm' && (ss.restMidRemainingMs ?? 0) > 0) return KNIGHT_REST_MID_MS - ss.restMidRemainingMs!
  if (boss.state === 'telegraph' && ss.restMidTeleKey === 'rest_from_rearm' && (ss.restMidTeleRemainingMs ?? 0) > 0) return KNIGHT_REST_MID_MS - ss.restMidTeleRemainingMs!
  return null
}

/** 部位の点の動かす量から、絵の任意の画素の動かす量 (距離の逆数の2乗の重み・点の上ではちょうどその点の量)。 */
export function idwOffset(points: readonly Pt[], offsets: readonly (readonly [number, number])[], x: number, y: number): [number, number] {
  let sw = 0, sx = 0, sy = 0
  for (let i = 0; i < points.length; i++) {
    const d2 = (x - points[i][0]) ** 2 + (y - points[i][1]) ** 2
    if (d2 < 1e-6) return [offsets[i][0], offsets[i][1]]
    const w = 1 / d2
    sw += w; sx += w * offsets[i][0]; sy += w * offsets[i][1]
  }
  return sw > 0 ? [sx / sw, sy / sw] : [0, 0]
}

export const TWEEN_MESH_COLS = 16
export const TWEEN_MESH_ROWS = 16
export type TweenMesh = { w: number; h: number; cols: number; rows: number; off: [number, number][] }
export function buildTweenMesh(plan: TweenPlan, w: number, h: number, cols = TWEEN_MESH_COLS, rows = TWEEN_MESH_ROWS): TweenMesh {
  const off: [number, number][] = []
  for (let j = 0; j <= rows; j++) for (let i = 0; i <= cols; i++) off.push(idwOffset(plan.points, plan.offsets, (i / cols) * w, (j / rows) * h))
  const m: TweenMesh = { w, h, cols, rows, off }
  // 網目の三角形の中は一次の補間なので、部位の点の上で IDW の値からずれる。残りを IDW で網目へ足し戻し、点の上で合うまで繰り返す。
  for (let k = 0; k < 12; k++) {
    let worst = 0
    const res = plan.points.map((p, i): [number, number] => {
      const d = meshOffsetAt(m, p[0], p[1])
      const rx = plan.offsets[i][0] - d[0], ry = plan.offsets[i][1] - d[1]
      worst = Math.max(worst, Math.hypot(rx, ry))
      return [rx, ry]
    })
    if (worst < 0.05) break
    for (let j = 0; j <= rows; j++) for (let i = 0; i <= cols; i++) {
      const c = idwOffset(plan.points, res, (i / cols) * w, (j / rows) * h), o = off[j * (cols + 1) + i]
      o[0] += c[0]; o[1] += c[1]
    }
  }
  return m
}
/** 網目で実際に描かれる動かす量 (三角形ごとの一次の補間 = 描く絵と同じ)。測るための probe 用。 */
export function meshOffsetAt(m: TweenMesh, x: number, y: number): [number, number] {
  const cw = m.w / m.cols, ch = m.h / m.rows
  const i = Math.max(0, Math.min(m.cols - 1, Math.floor(x / cw))), j = Math.max(0, Math.min(m.rows - 1, Math.floor(y / ch)))
  const u = x / cw - i, v = y / ch - j
  const at = (di: number, dj: number) => m.off[(j + dj) * (m.cols + 1) + i + di]
  const o00 = at(0, 0), o10 = at(1, 0), o01 = at(0, 1), o11 = at(1, 1)
  // 描く時の分け方と同じ: (00,10,11) と (00,11,01)
  if (u >= v) return [o00[0] + u * (o10[0] - o00[0]) + v * (o11[0] - o10[0]), o00[1] + u * (o10[1] - o00[1]) + v * (o11[1] - o10[1])]
  return [o00[0] + v * (o01[0] - o00[0]) + u * (o11[0] - o01[0]), o00[1] + v * (o01[1] - o00[1]) + u * (o11[1] - o01[1])]
}

/** B の絵を網目で曲げて描く。ctx は sprites.ts が向きと回転をかけた後の空間 (drawImage(img, rect…) と同じ所)。 */
export function drawTweenWarp(
  ctx: CanvasRenderingContext2D, img: CanvasImageSource, rect: { x: number; y: number; width: number; height: number }, m: TweenMesh,
): void {
  const kx = rect.width / m.w, ky = rect.height / m.h
  // 三角形の継ぎ目が透けないよう、画面で 0.75px ぶん外へ広げて切り抜く。
  const pad = 0.75 / Math.max(1e-6, Math.min(kx, ky))
  const cw = m.w / m.cols, ch = m.h / m.rows
  const vert = (i: number, j: number): [number, number, number, number] => {
    const o = m.off[j * (m.cols + 1) + i]
    return [i * cw, j * ch, i * cw + o[0], j * ch + o[1]]
  }
  const tri = (a: number[], b: number[], c: number[]): void => {
    const [x0, y0, u0, v0] = a, [x1, y1, u1, v1] = b, [x2, y2, u2, v2] = c
    const den = (x1 - x0) * (y2 - y0) - (x2 - x0) * (y1 - y0)
    if (Math.abs(den) < 1e-9) return
    const ta = ((u1 - u0) * (y2 - y0) - (u2 - u0) * (y1 - y0)) / den
    const tc = ((u2 - u0) * (x1 - x0) - (u1 - u0) * (x2 - x0)) / den
    const tb = ((v1 - v0) * (y2 - y0) - (v2 - v0) * (y1 - y0)) / den
    const td = ((v2 - v0) * (x1 - x0) - (v1 - v0) * (x2 - x0)) / den
    const te = u0 - ta * x0 - tc * y0, tf = v0 - tb * x0 - td * y0
    const gx = (u0 + u1 + u2) / 3, gy = (v0 + v1 + v2) / 3
    const grow = (u: number, v: number): [number, number] => {
      const dx = u - gx, dy = v - gy, d = Math.hypot(dx, dy) || 1
      return [u + dx / d * pad, v + dy / d * pad]
    }
    const sx0 = Math.max(0, Math.min(x0, x1, x2) - pad * 2), sy0 = Math.max(0, Math.min(y0, y1, y2) - pad * 2)
    const sx1 = Math.min(m.w, Math.max(x0, x1, x2) + pad * 2), sy1 = Math.min(m.h, Math.max(y0, y1, y2) + pad * 2)
    if (sx1 <= sx0 || sy1 <= sy0) return
    ctx.save()
    ctx.beginPath()
    ctx.moveTo(...grow(u0, v0)); ctx.lineTo(...grow(u1, v1)); ctx.lineTo(...grow(u2, v2))
    ctx.closePath()
    ctx.clip()
    ctx.transform(ta, tb, tc, td, te, tf)
    ctx.drawImage(img, sx0, sy0, sx1 - sx0, sy1 - sy0, sx0, sy0, sx1 - sx0, sy1 - sy0)
    ctx.restore()
  }
  ctx.save()
  ctx.translate(rect.x, rect.y)
  ctx.scale(kx, ky)
  for (let j = 0; j < m.rows; j++) {
    for (let i = 0; i < m.cols; i++) {
      const a = vert(i, j), b = vert(i + 1, j), c = vert(i + 1, j + 1), d = vert(i, j + 1)
      tri(a, b, c)
      tri(a, c, d)
    }
  }
  ctx.restore()
}
